import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/brand/StatusBadge";
import SupplyDialog from "@/components/supplies/SupplyDialog";
import ContractDialog from "@/components/supplies/ContractDialog";
import QuoteDialog from "@/components/quotes/QuoteDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, Loader2, Flame, Zap, ChevronDown, ChevronRight, Pencil, RotateCcw, Archive,
  Eye, EyeOff, FileSignature, CalendarClock, FileText,
} from "lucide-react";
import { expiryInfo, daysLabel, contractStatusTone } from "@/lib/scadenze";

function ExpiryBadge({ dateStr }) {
  const info = expiryInfo(dateStr);
  if (info.level === "none") return <span className="text-xs text-slate-400">Nessuna scadenza</span>;
  return (
    <StatusBadge type={info.tone} icon={CalendarClock} testid="expiry-badge">
      {info.label} · {daysLabel(info.days)}
    </StatusBadge>
  );
}

function SupplyCard({ supply, customerId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [contracts, setContracts] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [editSupply, setEditSupply] = useState(false);
  const [contractDlg, setContractDlg] = useState(null); // {mode, contract}
  const [quoteFor, setQuoteFor] = useState(null); // contract to quote (commodity)
  const Icon = supply.type === "Luce" ? Zap : Flame;
  const tone = supply.type === "Luce" ? "text-warning" : "text-brand";

  const loadContracts = useCallback(async () => {
    const { data } = await api.get(`/supplies/${supply.id}/contracts`);
    setContracts(data);
  }, [supply.id]);

  useEffect(() => { if (open && contracts === null) loadContracts(); }, [open, contracts, loadContracts]);

  const current = supply.current_contract;

  const doArchive = async () => {
    try { await api.post(`/supplies/${supply.id}/archive`); toast.success("Fornitura archiviata"); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="ds-card overflow-hidden" data-testid={`supply-card-${supply.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <button onClick={() => setOpen((v) => !v)} className="flex flex-1 items-center gap-3 text-left" data-testid={`supply-toggle-${supply.id}`}>
          {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
          <span className={`flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 ${tone}`}><Icon className="h-5 w-5" /></span>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-800">{supply.type}</span>
              <span className="inline-flex items-center gap-1 font-mono text-xs text-slate-500">
                {reveal ? supply.pod_pdr : supply.pod_pdr_masked}
                <span role="button" onClick={(e) => { e.stopPropagation(); setReveal((v) => !v); }} className="text-slate-400 hover:text-brand" data-testid={`supply-reveal-${supply.id}`}>
                  {reveal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </span>
              </span>
            </div>
            <div className="mt-0.5 text-xs text-slate-500">
              {supply.comune ? `${supply.comune}${supply.provincia ? ` (${supply.provincia})` : ""} · ` : ""}
              {supply.type === "Luce" ? `${supply.power_kw ?? "—"} kW · ${supply.annual_consumption ?? "—"} kWh/anno` : `${supply.annual_consumption ?? "—"} Smc/anno`}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2">
          {current ? <ExpiryBadge dateStr={current.economic_end_date} /> : <StatusBadge type="neutral">Nessun contratto</StatusBadge>}
          <StatusBadge type={supply.status === "Attiva" ? "success" : supply.status === "Cessata" ? "danger" : "warning"}>{supply.status}</StatusBadge>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-slate-50/60 p-4" data-testid={`supply-detail-${supply.id}`}>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h4 className="ds-section-title flex items-center gap-2"><FileSignature className="h-4 w-4 text-brand" /> Storico contratti</h4>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditSupply(true)} className="gap-1.5" data-testid={`edit-supply-${supply.id}`}><Pencil className="h-3.5 w-3.5" /> Fornitura</Button>
              <Button size="sm" onClick={() => setContractDlg({ mode: "create" })} className="gap-1.5" data-testid={`add-contract-${supply.id}`}><Plus className="h-3.5 w-3.5" /> Contratto</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="outline" className="gap-1.5 text-danger hover:text-danger" data-testid={`archive-supply-${supply.id}`}><Archive className="h-3.5 w-3.5" /></Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-white">
                  <AlertDialogHeader><AlertDialogTitle>Archiviare la fornitura?</AlertDialogTitle>
                    <AlertDialogDescription>La fornitura e i contratti restano nello storico ma non saranno più attivi.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={doArchive}>Archivia</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          {contracts === null ? (
            <div className="py-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-brand" /></div>
          ) : contracts.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-500">Nessun contratto registrato. Aggiungi il primo contratto.</div>
          ) : (
            <div className="space-y-2">
              {contracts.map((c) => (
                <div key={c.id} className="rounded-lg border border-border bg-white p-3" data-testid={`contract-row-${c.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-slate-800">{c.supplier || "Fornitore n.d."}</span>
                        <span className="text-sm text-slate-500">{c.offer_name || "—"}</span>
                        <StatusBadge type={contractStatusTone(c.status)}>{c.status}</StatusBadge>
                        {c.previous_contract_id && <StatusBadge type="info" icon={RotateCcw}>Rinnovo</StatusBadge>}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500">
                        <span>Scadenza: {c.economic_end_date ? new Date(c.economic_end_date).toLocaleDateString("it-IT") : "—"}</span>
                        {c.price_type && <span>Prezzo: {c.price_type}{c.energy_price != null ? ` · ${c.energy_price} ${c.price_unit || ""}` : ""}</span>}
                        {c.fixed_fee != null && <span>Quota fissa: {c.fixed_fee} €</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <ExpiryBadge dateStr={c.economic_end_date} />
                      <Button size="sm" variant="ghost" onClick={() => setQuoteFor(c)} title="Genera preventivo" data-testid={`quote-contract-${c.id}`}><FileText className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setContractDlg({ mode: "edit", contract: c })} data-testid={`edit-contract-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      {c.status !== "Rinnovato" && c.status !== "Annullato" && (
                        <Button size="sm" variant="outline" onClick={() => setContractDlg({ mode: "renew", contract: c })} className="gap-1.5" data-testid={`renew-contract-${c.id}`}><RotateCcw className="h-3.5 w-3.5" /> Rinnova</Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <SupplyDialog open={editSupply} onOpenChange={setEditSupply} customerId={customerId} supply={supply} onSaved={() => onChanged?.()} />
      {contractDlg && (
        <ContractDialog open={!!contractDlg} onOpenChange={(v) => !v && setContractDlg(null)} mode={contractDlg.mode}
          supply={supply} contract={contractDlg.contract}
          onSaved={() => { setContractDlg(null); loadContracts(); onChanged?.(); }} />
      )}
      {quoteFor && (
        <QuoteDialog open={!!quoteFor} onOpenChange={(v) => !v && setQuoteFor(null)} customerId={customerId}
          prefill={{ category: "Commodity", product: supply.type, supply_id: supply.id, supply_contract_id: quoteFor.id }}
          onSaved={() => { setQuoteFor(null); onChanged?.(); }} />
      )}
    </div>
  );
}

export default function SuppliesPanel({ customerId, onChanged }) {
  const [supplies, setSupplies] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/customers/${customerId}/supplies`); setSupplies(data); }
    catch (e) { setSupplies([]); }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged?.(); };

  return (
    <div className="space-y-4" data-testid="supplies-panel">
      <div className="flex items-center justify-between">
        <h3 className="ds-section-title flex items-center gap-2"><Flame className="h-4 w-4 text-brand" /> Forniture Luce/Gas</h3>
        <Button size="sm" onClick={() => setAddOpen(true)} className="gap-1.5" data-testid="add-supply-button"><Plus className="h-4 w-4" /> Aggiungi fornitura</Button>
      </div>

      {supplies === null ? (
        <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-brand" /></div>
      ) : supplies.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-slate-500">
          <Flame className="h-8 w-8 text-slate-300" />
          <div className="font-display font-semibold text-slate-700">Nessuna fornitura</div>
          <p className="max-w-sm text-sm">Aggiungi una fornitura Luce o Gas per gestire contratti, condizioni economiche e scadenze.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {supplies.map((s) => <SupplyCard key={s.id} supply={s} customerId={customerId} onChanged={refresh} />)}
        </div>
      )}

      <SupplyDialog open={addOpen} onOpenChange={setAddOpen} customerId={customerId} onSaved={refresh} />
    </div>
  );
}
